import { createContext, useContext, useMemo } from 'react';

import { LIVE_ACCOUNT_SOURCE, LIVE_SOURCE } from '@/features/storefront/data/live';
import type {
  StorefrontAccountSource,
  StorefrontDataSource,
} from '@/features/storefront/data/source';

/**
 * The shop a page is reading from, and the shopper's own data inside it.
 *
 * Defaulted to the live shop rather than left null, which is what keeps this
 * refactor invisible to everything that was already working: a page rendered
 * outside any provider - the dashboard's theme customizer, a unit test that
 * mounts one page - behaves exactly as it did before the source existed.
 */
export const StorefrontDataContext = createContext<StorefrontDataSource>(LIVE_SOURCE);
export const StorefrontAccountContext =
  createContext<StorefrontAccountSource>(LIVE_ACCOUNT_SOURCE);

export function useStorefrontData(): StorefrontDataSource {
  return useContext(StorefrontDataContext);
}

export function useStorefrontAccountData(): StorefrontAccountSource {
  return useContext(StorefrontAccountContext);
}

/**
 * The cache namespace of the shop being read.
 *
 * Every storefront query key starts with these two parts, so a preview's
 * catalog and a merchant's own catalog can both be in the cache at once
 * without either answering for the other. The `'storefront'` head is kept
 * because things outside the storefront invalidate by it - applying a theme
 * clears `['storefront']` and means all of it.
 */
export function storefrontScope(source: { key: string }, language?: string | null): [string, string] {
  // The language rides in the second part rather than as a third, so every
  // key keeps its shape (`[...scope, 'cart']`) and a query outside any
  // storefront shell - the customiser, a unit test - keys exactly as before.
  return ['storefront', language ? `${source.key}|${language}` : source.key];
}

/**
 * The language the shop's queries are asked in (docs/i18n.md §9).
 *
 * Every storefront response is localised by the server, so a cache keyed
 * without the language would hand the English catalogue to a shopper who just
 * switched to Hindi. The shell provides it; `null` - no shell - keys as before.
 */
export const StorefrontQueryLanguageContext = createContext<string | null>(null);

/** The scope of the shop this component is inside. */
export function useStorefrontScope(): [string, string] {
  const source = useStorefrontData();
  const language = useContext(StorefrontQueryLanguageContext);
  return useMemo(() => storefrontScope(source, language), [source, language]);
}
